
import React, { useState, useEffect, useCallback } from 'react';
import { 
    ShieldCheck, Search, Trash2, Loader2, Check, UserPlus, 
    ShieldAlert, Users, Plus, Info, X, UserCheck, ChevronRight, Link2, UserMinus, ShieldQuestion,
    AlertTriangle
} from 'lucide-react';
import { searchEntities, grantAccess, revokeAccess, listAccess, assignModerator, unassignModerator } from '../services/api';
import { AccessInfo, AccessRecord, SearchEntity } from '../types';

interface AccessSettingsProps {
    access: AccessInfo | null;
}

type ConfirmType = 'revoke' | 'unassign';

export const AccessSettings: React.FC<AccessSettingsProps> = ({ access }) => {
    const [activeRole, setActiveRole] = useState<'creator' | 'moderator'>('creator');
    const [accessList, setAccessList] = useState<AccessRecord[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Main Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchEntity[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Moderator Search State (Independent)
    const [modSearchQuery, setModSearchQuery] = useState('');
    const [modSearchResults, setModSearchResults] = useState<SearchEntity[]>([]);
    const [isModSearching, setIsModSearching] = useState(false);
    
    // Action State
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [selectingModeratorFor, setSelectingModeratorFor] = useState<AccessRecord | null>(null);

    // Confirmation Modal State
    const [confirmAction, setConfirmAction] = useState<{
        type: ConfirmType;
        record: AccessRecord;
        title: string;
        description: string;
    } | null>(null);

    const loadAccess = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listAccess(activeRole);
            setAccessList(res.result || []);
        } catch (e) {
            console.error("Failed to load access list:", e);
        } finally {
            setLoading(false);
        }
    }, [activeRole]);

    useEffect(() => { loadAccess(); }, [loadAccess]);

    // Handle Main Search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                performSearch(searchQuery.trim(), false);
            } else {
                setSearchResults([]);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Handle Moderator Search (Modal)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (modSearchQuery.trim().length >= 2) {
                performSearch(modSearchQuery.trim(), true);
            } else {
                setModSearchResults([]);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [modSearchQuery]);

    const performSearch = async (query: string, isMod: boolean) => {
        if (isMod) setIsModSearching(true); else setIsSearching(true);
        try {
            const res = await searchEntities(query);
            if (isMod) {
                setModSearchResults((res.result || []).filter(e => e.type === 'user'));
            } else {
                setSearchResults(res.result || []);
            }
        } catch (e) {
            console.error("Search failed:", e);
        } finally {
            if (isMod) setIsModSearching(false); else setIsSearching(false);
        }
    };

    const getPrefixedId = (id: string, type: string) => {
        const cleanId = id.replace(/^(u_|g_|d_)/, '');
        switch(type.toLowerCase()) {
            case 'user': return `u_${cleanId}`;
            case 'group': return `g_${cleanId}`;
            case 'department': return `d_${cleanId}`;
            default: return id;
        }
    };

    const handleGrantRole = async (entity: SearchEntity) => {
        setActionLoading(entity.id);
        const prefixedId = getPrefixedId(entity.id, entity.type);
        try {
            await grantAccess({ entityId: prefixedId, role: activeRole });
            setSearchQuery('');
            setSearchResults([]);
            await loadAccess();
        } catch (e: any) {
            alert("Ошибка при выдаче прав: " + (e.message || "Неизвестная ошибка"));
        } finally { setActionLoading(null); }
    };

    const handleAssignModerator = async (moderatorEntity: SearchEntity) => {
        if (!selectingModeratorFor) return;
        
        setActionLoading(moderatorEntity.id);
        const modPrefixedId = getPrefixedId(moderatorEntity.id, moderatorEntity.type);
        
        try {
            await grantAccess({ entityId: modPrefixedId, role: 'moderator' });
            await assignModerator({ 
                creatorEntityId: selectingModeratorFor.entityId, 
                moderatorId: modPrefixedId 
            });
            
            setSelectingModeratorFor(null);
            setModSearchQuery('');
            setModSearchResults([]);
            await loadAccess();
        } catch (e: any) {
            alert("Ошибка при назначении модератора: " + (e.message || "Неизвестная ошибка"));
        } finally {
            setActionLoading(null);
        }
    };

    const requestUnassign = (record: AccessRecord) => {
        setConfirmAction({
            type: 'unassign',
            record,
            title: 'Открепить модератора',
            description: `Вы уверены, что хотите открепить модератора от автора "${record.name || record.entityName}"?`
        });
    };

    const requestRevoke = (record: AccessRecord) => {
        const roleLabel = activeRole === 'creator' ? 'Автора' : 'Модератора';
        setConfirmAction({
            type: 'revoke',
            record,
            title: `Отозвать роль ${roleLabel}`,
            description: `Вы уверены, что хотите отозвать права доступа у "${record.name || record.entityName}"?`
        });
    };

    const handleConfirmedAction = async () => {
        if (!confirmAction) return;
        const { type, record } = confirmAction;
        
        setActionLoading(record.entityId + (type === 'unassign' ? '_unassign' : ''));
        setConfirmAction(null);

        try {
            if (type === 'revoke') {
                await revokeAccess({ entityId: record.entityId, role: activeRole });
            } else {
                await unassignModerator({ creatorEntityId: record.entityId });
            }
            await loadAccess();
        } catch (e: any) {
            alert("Ошибка выполнения действия: " + (e.message || "Неизвестная ошибка"));
        } finally { 
            setActionLoading(null); 
        }
    };

    const translateType = (type: string) => {
        const t = (type || '').toLowerCase();
        if (t === 'user' || t === 'сотрудник') return 'Сотрудник';
        if (t === 'department' || t === 'отдел') return 'Отдел';
        if (t === 'group' || t === 'группа') return 'Группа';
        return type;
    };

    const getEntityInfoFromId = (id: string) => {
        if (id.startsWith('u_')) return 'user';
        if (id.startsWith('g_')) return 'group';
        if (id.startsWith('d_')) return 'department';
        return 'unknown';
    };

    const getAvatarUrl = (photo?: string, name?: string) => {
        return photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=f1f5f9&color=3b82f6`;
    };

    return (
        <div className="space-y-4 animate-in fade-in duration-500 max-w-6xl mx-auto pb-10">
            {/* Header Section */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/20 text-white">
                        <ShieldCheck className="w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-slate-900 tracking-tight">Управление доступом</h2>
                        <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-0.5">Bitrix24 Social Poster</p>
                    </div>
                </div>
                
                <div className="flex p-1 bg-slate-100 rounded-xl border border-slate-200">
                    <button 
                        onClick={() => setActiveRole('creator')} 
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeRole === 'creator' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <UserPlus className="w-3.5 h-3.5" /> Авторы
                    </button>
                    <button 
                        onClick={() => setActiveRole('moderator')} 
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeRole === 'moderator' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <Users className="w-3.5 h-3.5" /> Модераторы
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Search Sidebar */}
                <div className="lg:col-span-4 space-y-4">
                    <div className="bg-white p-5 rounded-[1.5rem] border border-slate-200 shadow-sm h-fit sticky top-24">
                        <h3 className="font-black text-slate-800 uppercase tracking-widest text-[9px] flex items-center gap-2 mb-4">
                            <Search className="w-3.5 h-3.5 text-blue-600" /> Поиск в Bitrix24
                        </h3>
                        
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                            <input 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all text-xs font-bold placeholder:text-slate-300"
                                placeholder="Сотрудник, отдел или группа..."
                            />
                        </div>

                        <div className="space-y-1.5 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
                            {isSearching ? (
                                <div className="flex flex-col items-center justify-center py-10 opacity-50">
                                    <Loader2 className="w-6 h-6 animate-spin text-blue-600 mb-2" />
                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Поиск...</span>
                                </div>
                            ) : searchResults.length > 0 ? searchResults.map(entity => (
                                <div key={entity.id} className="flex items-center justify-between p-2.5 bg-slate-50/50 rounded-xl border border-transparent hover:border-blue-100 hover:bg-white transition-all group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <img src={getAvatarUrl(entity.photo || entity.avatar, entity.name)} className="w-8 h-8 rounded-lg border border-white shadow-sm object-cover" alt="" />
                                        <div className="overflow-hidden">
                                            <p className="text-[11px] font-black text-slate-800 truncate leading-tight">{entity.name}</p>
                                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">{translateType(entity.type)}</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => handleGrantRole(entity)}
                                        disabled={actionLoading === entity.id}
                                        className="p-1.5 bg-blue-600 text-white rounded-lg shadow-md active:scale-90 disabled:opacity-50 transition-transform"
                                    >
                                        {actionLoading === entity.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            )) : searchQuery.length >= 2 ? (
                                <div className="text-center py-10">
                                    <ShieldQuestion className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ничего не нашли</p>
                                </div>
                            ) : (
                                <div className="text-center py-10">
                                    <Users className="w-8 h-8 text-slate-100 mx-auto mb-2" />
                                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-300">Введите имя</p>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 pt-5 border-t border-slate-50 flex gap-3">
                             <div className="w-8 h-8 bg-blue-50 text-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Info className="w-4 h-4" />
                             </div>
                             <p className="text-[10px] text-slate-400 font-bold leading-relaxed">
                                 Права, выданные <span className="text-slate-600 underline">отделам</span> или <span className="text-slate-600 underline">группам</span>, наследуются всеми участниками.
                             </p>
                        </div>
                    </div>
                </div>

                {/* Main List Area */}
                <div className="lg:col-span-8 space-y-4">
                    <div className="bg-white rounded-[1.5rem] border border-slate-200 shadow-xl overflow-hidden min-h-[500px] flex flex-col">
                        <div className="px-6 py-5 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                            <div className="flex items-center gap-3">
                                <Users className="w-5 h-5 text-blue-600" />
                                <h3 className="font-black text-slate-800 text-sm tracking-tight">
                                    {activeRole === 'creator' ? 'Авторы контента' : 'Общий список модераторов'}
                                </h3>
                            </div>
                            <span className="bg-white px-3 py-1 rounded-full text-[10px] font-black text-slate-400 uppercase tracking-widest border border-slate-100 shadow-sm">
                                {accessList.length} записей
                            </span>
                        </div>

                        {loading ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-20">
                                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                                <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">Получение данных...</p>
                            </div>
                        ) : accessList.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-20 text-center px-10">
                                <div className="w-16 h-16 bg-slate-50 rounded-[1.5rem] flex items-center justify-center mb-4">
                                    <ShieldAlert className="w-8 h-8 text-slate-200" />
                                </div>
                                <h4 className="font-black text-slate-800 text-sm mb-1">Список пуст</h4>
                                <p className="text-slate-400 text-xs font-medium max-w-[280px]">Используйте панель слева, чтобы добавить первого {activeRole === 'creator' ? 'автора' : 'модератора'}.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-50">
                                {accessList.map(record => (
                                    <div key={record.entityId} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:bg-slate-50/50 transition-all">
                                        {/* User Info */}
                                        <div className="flex items-center gap-4 flex-1 min-w-0">
                                            <div className="relative flex-shrink-0">
                                                <img src={getAvatarUrl(record.photo || record.avatar, record.name || record.entityName)} className="w-11 h-11 rounded-[1.25rem] border-2 border-white shadow-sm object-cover" alt="" />
                                                <div className={`absolute -bottom-1 -right-1 w-4.5 h-4.5 rounded-full border-2 border-white flex items-center justify-center shadow-sm ${activeRole === 'moderator' ? 'bg-indigo-600' : 'bg-blue-600'}`}>
                                                    <Check className="w-2.5 h-2.5 text-white" />
                                                </div>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-black text-slate-800 truncate tracking-tight">{record.name || record.entityName || `ID: ${record.entityId}`}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1.5 py-0.5 bg-slate-100 rounded-md">
                                                        {translateType(record.entityType || getEntityInfoFromId(record.entityId))}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Moderator Slot (Only for creators) */}
                                        {activeRole === 'creator' && (
                                            <div className="flex-shrink-0 flex items-center justify-center sm:justify-end px-4 py-2 bg-slate-50/80 rounded-2xl border border-slate-100 min-w-[220px]">
                                                {record.assignedModerator ? (
                                                    <div className="flex items-center justify-between w-full gap-3">
                                                        <div className="flex items-center gap-2.5 overflow-hidden">
                                                            <div className="relative">
                                                                <img src={getAvatarUrl(record.assignedModerator.photo, record.assignedModerator.name)} className="w-8 h-8 rounded-xl border border-white shadow-sm object-cover" alt="" />
                                                                <div className="absolute -top-1 -right-1 p-0.5 bg-green-500 rounded-full border border-white">
                                                                    <Check className="w-1.5 h-1.5 text-white" />
                                                                </div>
                                                            </div>
                                                            <div className="overflow-hidden">
                                                                <p className="text-[11px] font-black text-slate-800 leading-none truncate mb-1">
                                                                    {record.assignedModerator.name}
                                                                </p>
                                                                <p className="text-[8px] font-black text-indigo-500 uppercase tracking-widest">Модератор</p>
                                                            </div>
                                                        </div>
                                                        <button 
                                                            onClick={() => requestUnassign(record)}
                                                            disabled={actionLoading === record.entityId + '_unassign'}
                                                            className="p-1.5 text-slate-300 hover:text-rose-500 hover:bg-white rounded-lg transition-all active:scale-90"
                                                            title="Открепить модератора"
                                                        >
                                                            {actionLoading === record.entityId + '_unassign' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button 
                                                        onClick={() => setSelectingModeratorFor(record)}
                                                        className="w-full flex items-center justify-center gap-2 py-1.5 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:border-blue-400 hover:text-blue-600 hover:bg-white transition-all group/modbtn"
                                                    >
                                                        <Link2 className="w-3.5 h-3.5 group-hover/modbtn:rotate-12 transition-transform" />
                                                        <span className="text-[9px] font-black uppercase tracking-widest">Связать с модератором</span>
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="flex items-center justify-end gap-2 ml-2">
                                            <button 
                                                onClick={() => requestRevoke(record)}
                                                disabled={!!actionLoading}
                                                className="p-2.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all disabled:opacity-30 active:scale-90"
                                                title="Удалить из списка"
                                            >
                                                {actionLoading === record.entityId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {confirmAction && (
                <div className="fixed inset-0 z-[110] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-200">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                <AlertTriangle className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-2">{confirmAction.title}</h3>
                            <p className="text-slate-500 text-sm font-semibold leading-relaxed mb-8 px-2">
                                {confirmAction.description}
                            </p>
                            <div className="flex gap-4">
                                <button 
                                    onClick={() => setConfirmAction(null)}
                                    className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black rounded-2xl transition-all text-xs uppercase tracking-widest"
                                >
                                    Отмена
                                </button>
                                <button 
                                    onClick={handleConfirmedAction}
                                    className="flex-1 py-4 bg-rose-600 hover:bg-rose-700 text-white font-black rounded-2xl transition-all shadow-lg shadow-rose-200 active:scale-95 text-xs uppercase tracking-widest"
                                >
                                    Подтвердить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Moderator Selection Modal */}
            {selectingModeratorFor && (
                <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-200">
                        <div className="px-8 py-6 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-600/20">
                                    <UserCheck className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-black text-slate-900 text-lg tracking-tight">Выбор ответственного</h3>
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Для автора: {selectingModeratorFor.name || selectingModeratorFor.entityName}</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => { setSelectingModeratorFor(null); setModSearchQuery(''); setModSearchResults([]); }} 
                                className="p-2.5 text-slate-400 hover:text-rose-500 bg-white border border-slate-100 rounded-xl transition-all shadow-sm active:scale-90"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="p-8">
                            <div className="relative mb-6">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-300" />
                                <input 
                                    autoFocus
                                    value={modSearchQuery}
                                    onChange={(e) => setModSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-[1.25rem] outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all text-sm font-bold placeholder:text-slate-300"
                                    placeholder="Имя сотрудника..."
                                />
                            </div>

                            <div className="space-y-1.5 max-h-[350px] overflow-y-auto custom-scrollbar pr-1">
                                {isModSearching ? (
                                    <div className="flex flex-col items-center justify-center py-10 opacity-50">
                                        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-2" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Поиск в базе...</span>
                                    </div>
                                ) : modSearchResults.length > 0 ? modSearchResults.map(entity => (
                                    <button 
                                        key={entity.id}
                                        disabled={!!actionLoading}
                                        onClick={() => handleAssignModerator(entity)}
                                        className="w-full flex items-center justify-between p-3 hover:bg-blue-50/50 rounded-2xl border border-transparent hover:border-blue-100 transition-all group text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <img src={getAvatarUrl(entity.photo || entity.avatar, entity.name)} className="w-9 h-9 rounded-xl border border-white shadow-sm object-cover" alt="" />
                                            <div>
                                                <p className="text-[11px] font-black text-slate-800 leading-tight">{entity.name}</p>
                                                <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5">Сотрудник</p>
                                            </div>
                                        </div>
                                        {actionLoading === entity.id ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-transform translate-x-0 group-hover:translate-x-1" />}
                                    </button>
                                )) : modSearchQuery.length >= 2 ? (
                                    <p className="text-center text-slate-400 text-[10px] py-10 font-black uppercase">Никого не нашли</p>
                                ) : (
                                    <div className="text-center py-12">
                                        <Users className="w-12 h-12 text-slate-100 mx-auto mb-3" />
                                        <p className="text-slate-300 text-[10px] font-black uppercase tracking-widest">Введите имя для начала поиска</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
